/* -----------------Globals--------------- */
import { useMemo, useState } from "react";

import { cacheKeys } from "@constants/cache-keys";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { groupExamsByCategory } from "@helpers/exams";
/* -----------------Types--------------- */
import type { ExamDetails } from "@memorang/types";
import ChooseExamScreen from "@memorang/ui/components/ChooseExamScreen";
import Loader from "@memorang/ui/components/Loader";
import { useQueryClient } from "@tanstack/react-query";
import SearchNoResult from "../../../../assets/no-search-results.svg";
import type { NextButtonHandler } from "../types";

type Props = {
	filteredExams: ExamDetails[];
	categoryId?: string;
	levelId?: string;
	categoryHasState?: boolean;
	handleNextAction: NextButtonHandler;
	cta?: string;
};

export const ChooseExamContainer = ({
	filteredExams,
	categoryId,
	handleNextAction,
	cta,
}: Props) => {
	const [selectedExamId, setSelectedExamId] = useState<string>();
	const [searchValue, setSearchValue] = useState("");

	const queryClient = useQueryClient();

	const examsList =
		queryClient.getQueryData<ExamDetails[]>([cacheKeys.exams]) ?? [];

	const { handleSwitchExamContext } = useExamContext();

	const selectedExam = filteredExams.find(
		(exam) => exam.exam.id === selectedExamId,
	);

	const groupedExams = filteredExams ? groupExamsByCategory(filteredExams) : [];

	const sectionData = [...(groupedExams || [])].map((group) => ({
		title: group.title,
		data: group.data.map(({ exam }) => {
			return {
				name: exam.name,
				id: exam.id,
			};
		}),
	}));
	const categoryName = useMemo(() => {
		const exam = filteredExams.find((exam) =>
			exam.exam.category.some((c) => c.id === categoryId),
		);
		return exam?.exam.category.find((c) => c.id === categoryId)?.name;
	}, [filteredExams, categoryId]);

	const filteredSections = sectionData.map((section) => ({
		...section,
		data: section.data.filter((item) =>
			item.name.toLowerCase().includes(searchValue.toLowerCase()),
		),
	}));

	if (!examsList.length) {
		return <Loader />;
	}

	const finalFilteredSections = filteredSections.every(
		(section) => section.data.length === 0,
	)
		? []
		: filteredSections;

	const handleNext = async () => {
		if (selectedExam) {
			await handleSwitchExamContext(selectedExam);
			handleNextAction("examDate");
		}
	};

	const showSearch = examsList.length > 5;

	return (
		<ChooseExamScreen
			handleNext={handleNext}
			handleSelectExam={setSelectedExamId}
			filteredSections={finalFilteredSections}
			searchValue={searchValue}
			setSearchValue={setSearchValue}
			selectedExamId={selectedExamId}
			selectedExamName={selectedExam?.exam.name}
			emptySearchResultSvg={SearchNoResult}
			categoryName={categoryName}
			cta={cta}
			showSearch={showSearch}
		/>
	);
};
